import { useState } from 'react';
import type { CreateCustomerRequest, CustomerDTO } from '@six-pockets/shared';
import { TextField } from '@/components/ui';
import { ManageFormModal } from '@/features/manage/components/ManageFormModal';

interface CustomerFormModalProps {
  /** The customer being edited; null to add one. */
  customer: CustomerDTO | null;
  busy: boolean;
  error: string | null;
  onSave: (body: CreateCustomerRequest) => void;
  onClose: () => void;
}

const digitsOnly = (v: string) => v.replace(/\D/g, '');

/**
 * Add or edit a customer (Customers page): name and 10-digit mobile. The server capitalises the name. Mount it
 * only while open so the fields start fresh.
 */
export function CustomerFormModal({ customer, busy, error, onSave, onClose }: CustomerFormModalProps) {
  const [name, setName] = useState(customer?.name ?? '');
  const [phone, setPhone] = useState(customer?.phone ?? '');

  const unchanged = !!customer && name.trim() === customer.name && phone === customer.phone;
  const blocked = !name.trim()
    ? 'Enter a name'
    : !/^[6-9]\d{9}$/.test(phone)
      ? 'Enter a 10-digit mobile number'
      : unchanged
        ? 'Nothing changed'
        : undefined;

  return (
    <ManageFormModal
      title={customer ? 'Edit customer' : 'Add customer'}
      submitLabel={customer ? 'Save changes' : 'Add customer'}
      busy={busy}
      error={error}
      blockedReason={blocked}
      onSubmit={() => onSave({ name: name.trim(), phone })}
      onClose={onClose}
    >
      <TextField label="Name" placeholder="Customer name" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} data-autofocus />
      <TextField
        label="Mobile number"
        type="tel"
        inputMode="numeric"
        placeholder="10-digit number"
        prefix="+91"
        mono
        value={phone}
        onChange={(e) => setPhone(digitsOnly(e.target.value).slice(0, 10))}
      />
    </ManageFormModal>
  );
}
